import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { GridRowModes, DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { db } from "../../../firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc
} from "firebase/firestore";

const EditToolbar = React.memo(function EditToolbar(props) {
  const { rows } = props;

  const handleClick = () => {
    const id = rows.length ? rows.length + 1 : 1;
    const currentDate = new Date();
    props.setIsEditing(true);
    const newRow = {
      id,
      characters: "",
      words: "",
      sentences: "",
      recordedAt: currentDate,
      isNew: true
    };

    props.setRows(oldRows => {
      return [newRow].concat(oldRows);
    });

    props.setRowModesModel(oldModel => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "characters" }
    }));
  };

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleClick}
        disabled={props.isEditing}
      >
        Add record
      </Button>
    </GridToolbarContainer>
  );
});
const FullFeaturedCrudGrid = React.memo(function FullFeaturedCrudGrid({
  data,
  userId
}) {
  const [rows, setRows] = React.useState([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [openDialog, setOpenDialog] = React.useState(false);
  const [rowToDelete, setRowToDelete] = React.useState(null);
  const [docToDelete, setDocToDelete] = React.useState(null);

  React.useEffect(() => {
    const transformedData = data.map((item, index) => ({
      docId: item.id,
      id: index + 1,
      characters: item.ReadingSpeed_1,
      words: item.ReadingSpeed_2,
      sentences: item.ReadingSpeed_3,
      recordedAt: item.RecordedAtTime.toDate()
    }));
    setRows(transformedData);
  }, [data]);

  const handleRowEditStop = async (params, event) => {
    // if (
    //   params.reason === GridRowEditStopReasons.rowFocusOut ||
    //   params.reason === GridRowEditStopReasons.enterKeyDown
    // ) {
    setIsEditing(false);
  };

  const saveRowToFirebase = async rowData => {
    const readingSpeedRef = collection(db, "ReadingSpeed");
    const newDocRef = doc(readingSpeedRef);

    const firebaseData = {
      ReadingSpeed_1: rowData.characters,
      ReadingSpeed_2: rowData.words,
      ReadingSpeed_3: rowData.sentences,
      RecordedAtTime: rowData.recordedAt,
      User: doc(db, "UserData", userId)
    };
    await setDoc(newDocRef, firebaseData);

    const userDocRef = doc(db, "UserData", userId);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const updatedReadingSpeedRefs = [
        ...userDocSnap.data().ReadingSpeed,
        newDocRef
      ];
      await updateDoc(userDocRef, {
        ReadingSpeed: updatedReadingSpeedRefs
      });
    }

    return {
      ...rowData,
      docId: newDocRef.id,
      isNew: false
    };
  };

  const handleProcessRowUpdateError = error => {};

  const handleCellEditCommit = async (params, event) => {
    const { id, field, value } = params;
    // Update local state
    setRows(prevRows =>
      prevRows.map(row =>
        row.id === id
          ? {
              ...row,
              [field]: value
            }
          : row
      )
    );
  };

  const processRowUpdate = async editedRow => {
    const isValidData = ["characters", "words", "sentences"].some(
      field => !!editedRow[field]
    );
    // If no valid data and it's a new row, remove it from the state
    if (!isValidData && editedRow.isNew) {
      setRows(prevRows => prevRows.filter(row => row.id !== editedRow.id));
      return {
        ...editedRow
      };
    }
    if (isValidData) {
      // If it's a new row, save to Firebase
      if (editedRow.isNew) {
        editedRow = await saveRowToFirebase(editedRow);
      } else {
        await updateRowInFirebase(editedRow.docId, editedRow);
      }
      setRows(prevRows =>
        prevRows.map(row => (row.id === editedRow.id ? editedRow : row))
      );
      // Return the updated row, marking it as no longer "new"
      return {
        ...editedRow,
        isNew: false
      };
    } else {
      // If no valid data and it's a new row, remove it from the state
      if (editedRow.isNew) {
        setRows(prevRows => prevRows.filter(row => row.id !== editedRow.id));
      }
      return {
        ...editedRow
      };
    }
  };

  const updateRowInFirebase = async (id, rowData) => {
    const readingSpeedRef = doc(db, "ReadingSpeed", id);
    const firebaseData = {
      ReadingSpeed_1: rowData.characters,
      ReadingSpeed_2: rowData.words,
      ReadingSpeed_3: rowData.sentences,
      RecordedAtTime: rowData.recordedAt
    };

    await updateDoc(readingSpeedRef, firebaseData);
  };

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };
  const numberInputOnWheelPreventChange = e => {
    console.log("a");
    e.target.blur();

    e.stopPropagation();

    setTimeout(() => {
      e.target.focus();
    }, 0);
  };
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "characters",
      headerName: "Characters",
      flex: 1,
      editable: true,
      align: "center",
      type: "number",
      headerAlign: "center",
      renderCell: params => (
        <input
          type="number"
          value={params.value}
          onWheel={numberInputOnWheelPreventChange}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit"
          }}
        />
      ),
      headerAlign: "center"
    },
    {
      field: "words",
      headerName: "Words",
      flex: 1,
      editable: true,
      align: "center",
      type: "number",
      headerAlign: "center",
      renderCell: params => (
        <input
          type="number"
          value={params.value}
          onWheel={numberInputOnWheelPreventChange}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit"
          }}
        />
      ),
      headerAlign: "center"
    },
    {
      field: "sentences",
      headerName: "Sentences",
      flex: 1,
      editable: true,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <input
          type="number"
          value={params.value}
          onWheel={numberInputOnWheelPreventChange}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit"
          }}
        />
      ),
      headerAlign: "center"
    },
    {
      field: "recordedAt",
      headerName: "Recorded At",
      flex: 1,
      align: "center",
      headerAlign: "center",
      type: "date",
      editable: true
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <Button color="secondary" onClick={() => initiateDelete(params.row)}>
          Delete
        </Button>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true
    }
  ];

  const initiateDelete = id => {
    setRowToDelete(id.id);
    setDocToDelete(id.docId);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      try {
        const readingSpeedRef = doc(db, "ReadingSpeed", docToDelete);
        await deleteDoc(readingSpeedRef);

        const userDocRef = doc(db, "UserData", userId);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const updatedReadingSpeedRefs = userDocSnap
            .data()
            .ReadingSpeed.filter(ref => ref.id !== docToDelete);
          await updateDoc(userDocRef, {
            ReadingSpeed: updatedReadingSpeedRefs
          });
        }

        // Delete from local state
        setRows(prevRows => prevRows.filter(row => row.id !== rowToDelete));

        // Reset the rowToDelete state
        setRowToDelete(null);
      } catch (error) {
        console.error("Error deleting record: ", error);
      }
    }

    // Close the confirmation dialog
    setOpenDialog(false);
  };

  const cancelDelete = () => {
    setRowToDelete(null);
    setDocToDelete(null);
    setOpenDialog(false);
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        height: 350,
        width: "100%",
        "& .actions": {
          color: "text.secondary"
        },
        "& .textPrimary": {
          color: "text.primary"
        }
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        sx={{
          ".MuiDataGrid-selectedRowCount": {
            visibility: "hidden"
          }
        }}
        onEditCellChangeCommitted={handleCellEditCommit}
        pageSizeOptions={[5, 10, 15]}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "id",
                sort: "desc"
              }
            ]
          }
        }}
        slots={{
          toolbar: EditToolbar
        }}
        slotProps={{
          toolbar: {
            setRows,
            setRowModesModel,
            rows,
            saveRowToFirebase,
            isEditing,
            setIsEditing
          }
        }}
      />
      <Dialog
        open={openDialog}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default FullFeaturedCrudGrid;
